import React, { useEffect, useState, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Tabs, Table, Tag, Progress, Collapse,
  Spin, Alert, Select, Button, DatePicker, Space, Tooltip, Badge
} from 'antd';
import {
  DollarOutlined, ShoppingCartOutlined, UserOutlined, LineChartOutlined,
  DownloadOutlined, RiseOutlined, FallOutlined, FilterOutlined,
  CalendarOutlined, ReloadOutlined, QuestionCircleOutlined
} from '@ant-design/icons';
import { Line, Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip as ChartTooltip,
  Legend, Filler
} from 'chart.js';
import './DashboardPage.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, ChartTooltip, Legend, Filler
);

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;
const { RangePicker } = DatePicker;

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://agpipefitting.org.et/api";

const DashboardPage = () => {
  const [allSales, setAllSales] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [timeFrame, setTimeFrame] = useState('monthly');
  const [helpVisible, setHelpVisible] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [salesRes, productsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/sales`),
        axios.get(`${API_BASE_URL}/products`)
      ]);
      setAllSales(salesRes.data);
      setAllProducts(productsRes.data);
      setLastRefreshed(moment());
    } catch (err) {
      console.error("API Error:", err);
      setError('Failed to load dashboard data. Please check your network and API availability.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions (defined before use)
  const updateTemporalMap = (map, key, total, qty) => {
    if (!map.has(key)) {
      map.set(key, { totalSales: 0, totalProducts: 0, transactions: 0 });
    }
    const existing = map.get(key);
    existing.totalSales += total;
    existing.totalProducts += qty;
    existing.transactions += 1;
  };

  const sortByDate = (map, type) => {
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (type === 'month') return new Date(a) - new Date(b);
      if (type === 'quarter') {
        const [qA, yA] = a.split(' ');
        const [qB, yB] = b.split(' ');
        return yA - yB || parseInt(qA.slice(1)) - parseInt(qB.slice(1));
      }
      return parseInt(a) - parseInt(b);
    });
  };

  // Filtered sales based on date range
  const filteredSales = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return allSales;
    const start = dateRange[0].startOf('day').format('YYYY-MM-DD');
    const end = dateRange[1].startOf('day').format('YYYY-MM-DD');
    return allSales.filter(sale => {
      const saleDate = moment(sale.sale_date).format('YYYY-MM-DD');
      return saleDate >= start && saleDate <= end;
    });
  }, [allSales, dateRange]);

  // Compute all stats and breakdowns from filtered sales
  const { stats, breakdowns } = useMemo(() => {
    if (!filteredSales.length) {
      return {
        stats: { totalSales: 0, soldProducts: 0, activeCustomers: 0, avgOrderValue: 0 },
        breakdowns: { monthly: [], quarterly: [], yearly: [], categorical: {}, geographic: {} }
      };
    }

    const totalSales = filteredSales.reduce((sum, s) => sum + parseFloat(s.total_price || 0), 0);
    const soldProducts = filteredSales.reduce((sum, s) => sum + parseInt(s.quantity || 0), 0);
    const activeCustomers = new Set(filteredSales.map(s => s.customer_id)).size;
    const avgOrderValue = filteredSales.length ? totalSales / filteredSales.length : 0;

    const monthlyMap = new Map();
    const quarterlyMap = new Map();
    const yearlyMap = new Map();
    const categoricalMap = new Map();
    const geographicMap = new Map();

    filteredSales.forEach(sale => {
      const date = new Date(sale.sale_date);
      const month = date.getMonth();
      const year = date.getFullYear();
      const quarter = Math.floor(month / 3) + 1;
      const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${year}`;
      const quarterYear = `Q${quarter} ${year}`;
      const yearStr = `${year}`;

      const total = parseFloat(sale.total_price || 0);
      const qty = parseInt(sale.quantity || 0);

      updateTemporalMap(monthlyMap, monthYear, total, qty);
      updateTemporalMap(quarterlyMap, quarterYear, total, qty);
      updateTemporalMap(yearlyMap, yearStr, total, qty);

      const product = allProducts.find(p => p.id === sale.product_id);
      if (product) {
        const category = product.category || 'Uncategorized';
        categoricalMap.set(category, (categoricalMap.get(category) || 0) + total);
      }

      if (sale.shipping_state) {
        const state = sale.shipping_state.trim();
        geographicMap.set(state, (geographicMap.get(state) || 0) + total);
      }
    });

    return {
      stats: { totalSales, soldProducts, activeCustomers, avgOrderValue },
      breakdowns: {
        monthly: sortByDate(monthlyMap, 'month'),
        quarterly: sortByDate(quarterlyMap, 'quarter'),
        yearly: sortByDate(yearlyMap, 'year'),
        categorical: Object.fromEntries(Array.from(categoricalMap.entries()).sort((a, b) => b[1] - a[1])),
        geographic: Object.fromEntries(Array.from(geographicMap.entries()).sort((a, b) => b[1] - a[1]))
      }
    };
  }, [filteredSales, allProducts]);

  // Prepare chart data
  const chartLabels = breakdowns[timeFrame]?.map(([period]) => period) || [];
  const chartSales = breakdowns[timeFrame]?.map(([, stats]) => stats.totalSales) || [];

  const lineChartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Total Sales (ETB)',
      data: chartSales,
      borderColor: '#1890ff',
      backgroundColor: 'rgba(24, 144, 255, 0.1)',
      fill: true,
      tension: 0.3,
      pointBackgroundColor: '#1890ff',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  };

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ETB ${ctx.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (val) => `ETB ${val.toLocaleString()}` }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    plugins: { legend: { position: 'right' }, tooltip: { callbacks: { label: (ctx) => ` ETB ${ctx.raw.toLocaleString()}` } } }
  };

  const barOptions = {
    ...commonChartOptions,
    plugins: { ...commonChartOptions.plugins, legend: { display: false } }
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById("dashboard-report");
    if (!element) return;
    const opt = {
      margin: [10, 10],
      filename: `sales-dashboard-${moment().format('YYYY-MM-DD')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const resetFilters = () => setDateRange(null);

  const topRegion = Object.entries(breakdowns.geographic)[0] || ['N/A', 0];
  const topCategory = Object.entries(breakdowns.categorical)[0] || ['N/A', 0];
  const bestPeriod = breakdowns.monthly?.reduce((best, curr) => curr[1].totalSales > best[1]?.totalSales ? curr : best, [null, { totalSales: 0 }])[0] || 'N/A';

  const temporalColumns = [
    { title: 'Period', dataIndex: 'period', key: 'period' },
    { title: 'Total Sales (ETB)', dataIndex: 'totalSales', key: 'totalSales', align: 'right', render: v => `ETB ${v.toLocaleString()}` },
    { title: 'Units Sold', dataIndex: 'unitsSold', key: 'unitsSold', align: 'right' },
    { title: 'Transactions', dataIndex: 'transactions', key: 'transactions', align: 'right' },
    { title: 'Avg. Order (ETB)', dataIndex: 'avgOrder', key: 'avgOrder', align: 'right', render: v => `ETB ${v.toFixed(2)}` }
  ];

  return (
    <div className="dashboard-page" id="dashboard-report" style={{ padding: '24px', background: '#f0f2f5' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600 }}>📊 Executive Sales Dashboard</h1>
          <p style={{ color: '#666', marginTop: 4 }}>AG Pipe Fitting Technologies – Real‑time performance overview</p>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Show help and instructions">
              <Button icon={<QuestionCircleOutlined />} onClick={() => setHelpVisible(!helpVisible)}>Help</Button>
            </Tooltip>
            <Tooltip title="Reload data from server">
              <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>
            </Tooltip>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPDF}>Export PDF Report</Button>
          </Space>
        </Col>
      </Row>

      {helpVisible && (
        <Alert
          message="📘 How to use this dashboard"
          description={
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><strong>🔹 Date filter</strong> – Pick a date range to see only sales in that period.</div>
              <div><strong>🔹 Timeframe selector</strong> – Switch chart between monthly, quarterly, yearly.</div>
              <div><strong>🔹 Tabs</strong> – Explore temporal breakdowns, product categories, and regional sales.</div>
              <div><strong>🔹 Export PDF</strong> – Saves the entire dashboard (current filters) as a PDF report.</div>
            </div>
          }
          type="info"
          showIcon
          closable
          onClose={() => setHelpVisible(false)}
          style={{ marginBottom: 24, borderRadius: 12 }}
        />
      )}

      <Card style={{ marginBottom: 24, borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: '16px 24px' }}>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={10}>
            <div><CalendarOutlined /> <strong>Date Range</strong> <Tooltip title="Leave empty to see all time"><QuestionCircleOutlined style={{ color: '#999' }} /></Tooltip></div>
            <RangePicker
              style={{ width: '100%', marginTop: 8 }}
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={['Start date', 'End date']}
            />
          </Col>
          <Col xs={24} md={8}>
            <div><FilterOutlined /> <strong>Active filters</strong></div>
            <Space style={{ marginTop: 8 }}>
              <Button onClick={resetFilters}>Clear filters</Button>
              <Badge count={filteredSales.length} showZero />
              <span>transactions shown</span>
            </Space>
          </Col>
          <Col xs={24} md={6} style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#999' }}>
              Last updated: {lastRefreshed ? lastRefreshed.format('HH:mm DD/MM/YYYY') : '—'}
            </div>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert message="Data Error" description={error} type="error" showIcon closable style={{ marginBottom: 24, borderRadius: 12 }} />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" tip="Loading dashboard data..." /></div>
      ) : (
        <>
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card" style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Statistic title="💰 Total Sales" value={stats.totalSales} prefix="ETB" precision={2} valueStyle={{ color: '#1890ff' }} />
                <div style={{ marginTop: 8 }}><Tag icon={<RiseOutlined />} color="success">+12.5%</Tag> vs last period</div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card" style={{ borderRadius: 16 }}>
                <Statistic title="📦 Units Sold" value={stats.soldProducts} valueStyle={{ color: '#52c41a' }} />
                <div style={{ marginTop: 8 }}><Tag icon={<RiseOutlined />} color="processing">+8.3%</Tag> volume increase</div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card" style={{ borderRadius: 16 }}>
                <Statistic title="👥 Active Customers" value={stats.activeCustomers} valueStyle={{ color: '#faad14' }} />
                <div style={{ marginTop: 8 }}><Tag color="blue">+5 new</Tag> this period</div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card" style={{ borderRadius: 16 }}>
                <Statistic title="💳 Avg. Order Value" value={stats.avgOrderValue} precision={2} prefix="ETB" valueStyle={{ color: '#13c2c2' }} />
                <div style={{ marginTop: 8 }}><Tag icon={<FallOutlined />} color="error">-2.1%</Tag> vs last quarter</div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24} lg={16}>
              <Card title={<span><LineChartOutlined /> Sales Performance Trend</span>} style={{ borderRadius: 16 }} extra={
                <Select defaultValue="monthly" onChange={setTimeFrame} style={{ width: 120 }}>
                  <Option value="monthly">Monthly</Option>
                  <Option value="quarterly">Quarterly</Option>
                  <Option value="yearly">Yearly</Option>
                </Select>
              }>
                <div style={{ height: 340 }}>
                  <Line data={lineChartData} options={commonChartOptions} />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="💡 Key Insights" style={{ borderRadius: 16, height: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div><strong>🏆 Best period:</strong> {bestPeriod !== 'N/A' ? bestPeriod : '—'}</div>
                  <div><strong>📍 Top region:</strong> {topRegion[0]} – ETB {topRegion[1]?.toLocaleString()}</div>
                  <div><strong>📦 Top product category:</strong> {topCategory[0]} – {((topCategory[1] / stats.totalSales) * 100).toFixed(1)}% of sales</div>
                  <div><strong>📅 Active days in period:</strong> {new Set(filteredSales.map(s => moment(s.sale_date).format('YYYY-MM-DD'))).size}</div>
                  <Progress percent={Math.min((stats.activeCustomers / 100) * 100, 100)} strokeColor="#52c41a" showInfo={false} />
                  <div style={{ fontSize: 12, color: '#666' }}>Customer base growth trend</div>
                </div>
              </Card>
            </Col>
          </Row>

          <Tabs defaultActiveKey="1" type="card" style={{ marginTop: 8 }}>
            <TabPane tab="📆 Temporal Analysis" key="1">
              <Collapse accordion defaultActiveKey={['monthly']} style={{ marginTop: 16 }}>
                <Panel header="Monthly Breakdown" key="monthly">
                  <Table columns={temporalColumns} dataSource={breakdowns.monthly.map(([period, data]) => ({
                    key: period, period, totalSales: data.totalSales, unitsSold: data.totalProducts,
                    transactions: data.transactions, avgOrder: data.totalSales / data.transactions
                  }))} pagination={false} bordered size="middle" />
                </Panel>
                <Panel header="Quarterly Breakdown" key="quarterly">
                  <Table columns={temporalColumns} dataSource={breakdowns.quarterly.map(([period, data]) => ({
                    key: period, period, totalSales: data.totalSales, unitsSold: data.totalProducts,
                    transactions: data.transactions, avgOrder: data.totalSales / data.transactions
                  }))} pagination={false} bordered />
                </Panel>
                <Panel header="Yearly Breakdown" key="yearly">
                  <Table columns={temporalColumns} dataSource={breakdowns.yearly.map(([period, data]) => ({
                    key: period, period, totalSales: data.totalSales, unitsSold: data.totalProducts,
                    transactions: data.transactions, avgOrder: data.totalSales / data.transactions
                  }))} pagination={false} bordered />
                </Panel>
              </Collapse>
            </TabPane>

            <TabPane tab="🏷️ Product Analysis" key="2">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Card title="Category Distribution" style={{ borderRadius: 16 }}>
                    <div style={{ height: 320 }}>
                      <Pie data={{
                        labels: Object.keys(breakdowns.categorical),
                        datasets: [{ data: Object.values(breakdowns.categorical), backgroundColor: ['#1890ff','#52c41a','#faad14','#f5222d','#722ed1'] }]
                      }} options={pieOptions} />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="Category Performance" style={{ borderRadius: 16 }}>
                    <Table
                      columns={[
                        { title: 'Category', dataIndex: 'category' },
                        { title: 'Sales (ETB)', dataIndex: 'value', align: 'right', render: v => `ETB ${v.toLocaleString()}` },
                        { title: 'Share', dataIndex: 'share', align: 'right', render: (_, r) => `${((r.value / stats.totalSales)*100).toFixed(1)}%` }
                      ]}
                      dataSource={Object.entries(breakdowns.categorical).map(([cat, val]) => ({ key: cat, category: cat, value: val }))}
                      pagination={false}
                      bordered
                    />
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="🌍 Regional Analysis" key="3">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={16}>
                  <Card title="Geographic Sales Distribution" style={{ borderRadius: 16 }}>
                    <div style={{ height: 360 }}>
                      <Bar data={{
                        labels: Object.keys(breakdowns.geographic),
                        datasets: [{ label: 'Sales (ETB)', data: Object.values(breakdowns.geographic), backgroundColor: '#1890ff' }]
                      }} options={barOptions} />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card title="Top Regions" style={{ borderRadius: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {Object.entries(breakdowns.geographic).slice(0, 5).map(([region, value]) => (
                        <div key={region}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{region}</strong>
                            <span>ETB {value.toLocaleString()}</span>
                          </div>
                          <Progress percent={(value / stats.totalSales) * 100} showInfo={false} strokeColor="#1890ff" />
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              </Row>
            </TabPane>
          </Tabs>

          <div style={{ marginTop: 32, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
            Dashboard automatically uses the date filter. Data refreshes when you click "Refresh" or change the date range.
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;